import {
  Button,
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
  Input,
  toast,
} from '@sitewise/ui';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { BookmarkPlus, ChevronDown, Star, Trash2 } from 'lucide-react';
import { useState } from 'react';

import {
  createSavedView,
  deleteSavedView,
  listSavedViews,
  updateSavedView,
  type SavedView,
  type SavedViewScope,
} from '../../../api/saved-views.js';
import { ApiError } from '../../../lib/api-client.js';

/**
 * Polish Phase 2 — saved views dropdown for the list pages. The
 * parent owns the filter state and exposes serialize/apply callbacks
 * so this component stays generic between work orders and violations.
 */
interface SavedViewsBarProps {
  scope: SavedViewScope;
  currentFilters: Record<string, unknown>;
  onApply: (view: SavedView) => void;
}

export function SavedViewsBar({ scope, currentFilters, onApply }: SavedViewsBarProps) {
  const queryClient = useQueryClient();
  const viewsQuery = useQuery({
    queryKey: ['saved-views', scope],
    queryFn: () => listSavedViews(scope),
    staleTime: 60_000,
  });
  const [open, setOpen] = useState(false);
  const [name, setName] = useState('');

  const createMut = useMutation({
    mutationFn: () =>
      createSavedView({ scope, name: name.trim(), filters: currentFilters }),
    onSuccess: () => {
      toast.success(`Saved view "${name}" created`);
      setName('');
      setOpen(false);
      void queryClient.invalidateQueries({ queryKey: ['saved-views', scope] });
    },
    onError: (err) => {
      toast.error(err instanceof ApiError ? err.message : 'Could not save view');
    },
  });

  const deleteMut = useMutation({
    mutationFn: (id: string) => deleteSavedView(id),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ['saved-views', scope] });
    },
  });

  const defaultMut = useMutation({
    mutationFn: (id: string) => updateSavedView(id, { isDefault: true }),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ['saved-views', scope] });
    },
  });

  const views = viewsQuery.data ?? [];

  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <Button variant="outline" size="sm">
          <BookmarkPlus className="h-4 w-4" />
          Views
          <ChevronDown className="h-3.5 w-3.5" />
        </Button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="start" className="w-72">
        {views.length === 0 ? (
          <DropdownMenuItem disabled>No saved views yet</DropdownMenuItem>
        ) : (
          views.map((v) => (
            <DropdownMenuItem
              key={v.id}
              onSelect={(e) => {
                e.preventDefault();
                onApply(v);
              }}
              className="flex items-center justify-between gap-2"
            >
              <button
                type="button"
                className="flex-1 text-left text-sm"
                onClick={() => onApply(v)}
              >
                {v.name}
              </button>
              <div className="flex shrink-0 items-center gap-1">
                <button
                  type="button"
                  className={v.isDefault ? 'text-warning' : 'text-fg-muted hover:text-fg'}
                  onClick={(e) => {
                    e.stopPropagation();
                    if (!v.isDefault) defaultMut.mutate(v.id);
                  }}
                  aria-label={v.isDefault ? 'Default view' : 'Make default'}
                >
                  <Star className="h-3.5 w-3.5" />
                </button>
                <button
                  type="button"
                  className="text-fg-muted hover:text-danger"
                  onClick={(e) => {
                    e.stopPropagation();
                    if (window.confirm(`Delete saved view "${v.name}"?`)) {
                      deleteMut.mutate(v.id);
                    }
                  }}
                  aria-label="Delete view"
                >
                  <Trash2 className="h-3.5 w-3.5" />
                </button>
              </div>
            </DropdownMenuItem>
          ))
        )}
        <DropdownMenuSeparator />
        <div className="space-y-2 px-2 py-1.5">
          <p className="text-xs font-medium text-fg-muted">Save current filters as…</p>
          <div className="flex gap-1.5">
            <Input
              value={name}
              onChange={(e) => setName(e.target.value)}
              placeholder="View name"
              className="h-8 text-xs"
            />
            <Button
              size="sm"
              disabled={!name.trim() || createMut.isPending}
              onClick={() => createMut.mutate()}
            >
              Save
            </Button>
          </div>
        </div>
        {void open}
      </DropdownMenuContent>
    </DropdownMenu>
  );
}
